/**
 * Ad-group create / edit — writes through Surface B (`management.adGroups`). Creating an ad group also
 * auto-creates its bid_adjustment and recomputes the campaign's flight window (server side). Budgets are
 * capped to the parent campaign. Includes the day-parting schedule and geo targeting.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { management, ApiError } from '../lib/api';
import { Text, Select, Check } from './CampaignForm';
import { DayPartingPicker } from '../components/DayPartingPicker';
import { defaultSchedule, normalizeSchedule, serializeDayParting, type DayPartingSchedule } from '../lib/day-parting';
import { GeoEditor } from '../components/GeoEditor';

type Form = Record<string, unknown>;
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const STRATEGY = ['moderate', 'aggressive', 'cost_effective', 'exact'];
const OBJECTIVES = ['DRIVE_WEBSITE_TRAFFIC', 'LEADS_GENERATION', 'BRAND_AWARENESS', 'ONLINE_PURCHASES', 'MOBILE_APP_INSTALL'];
/** "YYYY-MM-DD HH:mm:ss" → ['YYYY-MM-DD','HH:mm']. */
const splitDt = (dt: string): [string, string] => {
  const [d, t] = str(dt).split(' ');
  return [d ?? '', (t ?? '').slice(0, 5)];
};

export function AdGroupFormScreen(): JSX.Element {
  const params = useParams<{ id: string; campaignId: string }>();
  const editing = Boolean(params.id);
  const navigate = useNavigate();
  const [campaignId, setCampaignId] = useState(params.campaignId ?? '');
  const [caps, setCaps] = useState<{ overall: string; daily: string } | null>(null);
  const [dayParting, setDayParting] = useState<DayPartingSchedule>(defaultSchedule());
  const [f, setF] = useState<Form>({
    adgroup_name: '', start_date: '', start_time: '00:00', end_date: '', end_time: '23:59',
    overall_budget: '', daily_budget: '', bid: '', strategy_type: 'moderate',
    target_impressions: '0', target_clicks: '0', marketing_objective: 'DRIVE_WEBSITE_TRAFFIC',
    geo_type: 'COUNTRY', geo_data: 'US', radius: '10', dooh_screen_ids: '',
    is_active: true, turboboost_allow: false,
  });
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  // New: load the parent campaign for its budget caps.
  useEffect(() => {
    if (editing || !campaignId) return;
    let live = true;
    management.campaigns.get(campaignId).then(({ campaign: c }) => {
      if (live) setCaps({ overall: str(c.overall_budget), daily: str(c.daily_budget) });
    }).catch(() => { /* caps are a hint; the server enforces them */ });
    return () => { live = false; };
  }, [editing, campaignId]);

  // Edit: load the ad group + its bid_adjustment (target clicks/impressions live there).
  useEffect(() => {
    if (!editing || !params.id) return;
    let live = true;
    (async () => {
      try {
        const { ad_group: g } = await management.adGroups.get(params.id!);
        const ba = await management.bidAdjustments.get(params.id!).then((r) => r.bid_adjustment).catch(() => null);
        if (!live) return;
        setCampaignId(str(g.campaign_id));
        const [sd, stime] = splitDt(str(g.start_date_time));
        const [ed, etime] = splitDt(str(g.end_date_time));
        setDayParting(normalizeSchedule(g.day_parting)); // migrates legacy {start,end} records to the mask shape
        setF({
          adgroup_name: str(g.adgroup_name), start_date: sd, start_time: stime || '00:00', end_date: ed, end_time: etime || '23:59',
          overall_budget: str(g.overall_budget), daily_budget: str(g.daily_budget), bid: str(g.bid),
          strategy_type: str(g.strategy_type) || 'moderate',
          target_impressions: str(ba?.target_impressions ?? '0'), target_clicks: str(ba?.target_clicks ?? '0'),
          marketing_objective: str(g.marketing_objective) || 'DRIVE_WEBSITE_TRAFFIC',
          geo_type: str(g.geo_type) || 'COUNTRY', geo_data: str(g.geo_data_for_ui ?? g.geo_data), radius: str(g.radius) || '10',
          dooh_screen_ids: Array.isArray(g.dooh_screen_ids) ? (g.dooh_screen_ids as unknown[]).join(', ') : str(g.dooh_screen_ids),
          is_active: g.is_active === 'Y', turboboost_allow: g.turboboost_allow === 'Y',
        });
        setLoading(false);
      } catch (e) { if (live) { setError(e instanceof ApiError ? e.message : 'Could not load the ad group.'); setLoading(false); } }
    })();
    return () => { live = false; };
  }, [editing, params.id]);

  const submit = useCallback(async () => {
    setError(null);
    if (!campaignId) { setError('Missing campaign.'); return; }
    if (!f.adgroup_name) { setError('Ad group name is required.'); return; }
    if (Number(f.daily_budget) > Number(f.overall_budget)) { setError('Daily budget must be ≤ overall budget.'); return; }
    if (caps && Number(f.overall_budget) > Number(caps.overall)) { setError(`Overall budget can’t exceed the campaign’s (${caps.overall}).`); return; }
    setSaving(true);
    const body: Form = {
      campaign_id: campaignId, adgroup_name: f.adgroup_name,
      start_date: f.start_date, end_date: f.end_date,
      start_time: `${str(f.start_time)}:00`, end_time: `${str(f.end_time)}:00`,
      overall_budget: f.overall_budget, daily_budget: f.daily_budget, bid: f.bid,
      strategy_type: f.strategy_type, target_impressions: f.target_impressions, target_clicks: f.target_clicks,
      marketing_objective: f.marketing_objective, day_parting: serializeDayParting(dayParting),
      geo_type: f.geo_type, geo_data: f.geo_data, radius: f.radius,
      dooh_screen_ids: f.dooh_screen_ids, is_active: f.is_active ? 'Y' : 'N', turboboost_allow: f.turboboost_allow ? 'Y' : 'N',
    };
    try {
      if (editing && params.id) { await management.adGroups.update(params.id, body); navigate(`/adgroups/${params.id}`); }
      else { const { ad_group } = await management.adGroups.create(body); navigate(`/adgroups/${String(ad_group.id)}`); }
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Save failed.'); setSaving(false); }
  }, [f, dayParting, caps, campaignId, editing, params.id, navigate]);

  if (loading) return <div className="busy">Loading…</div>;

  return (
    <div className="mform">
      <h1>{editing ? 'Edit ad group' : 'New ad group'}</h1>
      <p className="sub">
        {editing ? 'Edits apply on the next loader run.' : 'A new ad group seeds its bid adjustment and extends the campaign’s flight window.'}
        {caps && !editing && <> Budgets are capped to the campaign (overall {caps.overall}, daily {caps.daily}).</>}
      </p>
      {error && <div className="banner error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <section>
        <h2>Basics</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="adgroup_name" label="Ad group name" full />
          <Text f={f} set={set} k="start_date" label="Start date" type="date" />
          <Text f={f} set={set} k="start_time" label="Start time" type="time" />
          <Text f={f} set={set} k="end_date" label="End date" type="date" />
          <Text f={f} set={set} k="end_time" label="End time" type="time" />
        </div>
      </section>

      <section>
        <h2>Budget &amp; bidding</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="overall_budget" label="Overall budget" type="number" hint={caps ? `≤ ${caps.overall}` : undefined} />
          <Text f={f} set={set} k="daily_budget" label="Daily budget" type="number" hint={caps ? `≤ ${caps.daily}` : 'Must be ≤ overall.'} />
          <Text f={f} set={set} k="bid" label="Bid" type="number" />
          <Select f={f} set={set} k="strategy_type" label="Strategy" options={STRATEGY} />
          <Text f={f} set={set} k="target_impressions" label="Target impressions" type="number" />
          <Text f={f} set={set} k="target_clicks" label="Target clicks" type="number" />
          <Select f={f} set={set} k="marketing_objective" label="Marketing objective" options={OBJECTIVES} full />
        </div>
      </section>

      <section>
        <h2>Day parting</h2>
        <DayPartingPicker value={dayParting} onChange={setDayParting} />
      </section>

      <section>
        <h2>Geo targeting</h2>
        <div className="mgrid">
          <GeoEditor
            geoType={str(f.geo_type)} geoData={str(f.geo_data)} radius={str(f.radius)}
            onChange={(v) => setF((p) => ({ ...p, geo_type: v.geo_type, geo_data: v.geo_data, radius: v.radius }))}
          />
        </div>
      </section>

      <section>
        <h2>Options</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="dooh_screen_ids" label="DOOH screen IDs" hint="Comma or space separated, optional." full />
          <Check f={f} set={set} k="turboboost_allow" label="Allow turbo-boost" />
          <Check f={f} set={set} k="is_active" label="Active" />
        </div>
      </section>

      <div className="mform-actions">
        <button className="button primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create ad group'}</button>
        <button className="button" onClick={() => navigate(editing && params.id ? `/adgroups/${params.id}` : `/campaigns/${campaignId}`)}>Cancel</button>
      </div>
    </div>
  );
}
